import { Composable, Inject, Setup, Vue } from '@natureos/vue-facing-decorator';

import type { ApiResponse, ApiService } from '@/common/services/api.service-new';
import type { MultiPolygonCoords } from '../hansen/hansen.types';
import { useEsaBiomassStore } from './esa-biomass.store';
import type { EsaBiomassLayerResponse, EsaBiomassStatsResponse } from './esa-biomass.types';

/**
 * ESA CCI Above-Ground Biomass V6.0 — continuous, Mg/ha. Layer metadata and stats are
 * served by the biomass API, while the returned per-epoch tile URLs remain on the
 * analytics infrastructure (Path A / GEE). The tile URLs point at JWT-protected
 * `/esa-biomass/tiles/{z}/{x}/{y}?year=YYYY` endpoints (Lambda@Edge verifies the bearer
 * on every tile GET); `ApiService` attaches the token automatically on the JSON calls,
 * and the tile fetcher routes through the raster service so the bearer rides on the tile
 * requests too.
 *
 * Contract: ~/.claude/contract/260625_esa_biomass.md
 */
@Composable()
export class useEsaBiomassService extends Vue {
  @Inject()
  private readonly api!: ApiService;

  @Setup(() => useEsaBiomassStore())
  public readonly biomassStore!: ReturnType<typeof useEsaBiomassStore>;

  /** Per-epoch tile-URL templates (all 10 years in one payload). Cached. */
  public async getEsaBiomassLayer(
    signal?: AbortSignal,
  ): Promise<ApiResponse<EsaBiomassLayerResponse>> {
    const cached = this.biomassStore.getLayer();
    if (cached) {
      return cached;
    }

    const res = await this.api.get<EsaBiomassLayerResponse>(
      `${import.meta.env.VITE_GEE_API}/esa-biomass/layer`,
      { signal },
    );

    if (res.isSuccess && res.data) {
      this.biomassStore.setLayer(res);
    }
    return res;
  }

  /** Polygon biomass stats (20 fixed bins + scalar summary, Mg/ha) for `year`. */
  public async getEsaBiomassStats(
    coords: MultiPolygonCoords,
    year: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<EsaBiomassStatsResponse>> {
    return this.api.post<EsaBiomassStatsResponse>(
      `${import.meta.env.VITE_GEE_API}/esa-biomass/stats`,
      { data: coords, year },
      { signal },
    );
  }
}
